//--------------------------------------------------------------------//
//******************** trunkConnectedAppsTab ***************************//
//--------------------------------------------------------------------//
// Manages trunk.connected_apps: a list of {app_id, phone_field_ids,
// name_field_id} entries used server-side (see sip-gudhub-server's
// server/sip-trunks/ConnectedAppsResolver.js) to match an inbound caller's
// number against items in a GudHub app, so the resolved item's name/id can
// be attached to the call. Whole-array replace on every add/edit/delete via
// SipTrunksService.update, same as trunk-numbers.tab.js's did_numbers.
import trunkConnectedAppsTemplateUrl from './trunk-connected-apps.template.html';

angular.module('sipTrunksTableModule')

.directive('trunkConnectedAppsTab', ['SipTrunksService', 'GhDialog', function (SipTrunksService, GhDialog) {
    return {
        restrict: 'E',
        scope: {
            trunk: '=',
            onUpdate: '&'
        },
        controller: ['$scope', 'localizationService', function ($scope, localizationService) {
            const dictionary = localizationService.getDictionary('account_settings');
            $scope.translate = (word) => localizationService.translate(word, dictionary);

            if (!Array.isArray($scope.trunk.connected_apps)) {
                $scope.trunk.connected_apps = [];
            }

            const normalizeId = function (value) {
                return String(value || '');
            };

            const safeApply = function () {
                $scope.$applyAsync();
            };

            // Cached by app_id so a trunk with several entries on the same
            // app (or a re-render after an unrelated field save) doesn't
            // re-fetch it every time - only cleared implicitly on a fresh
            // page load of this tab (a new directive instance).
            const appCache = {};

            async function getAppData(appId) {
                if (appCache[appId]) return appCache[appId];
                const app = await gudhub.getApp(appId);
                appCache[appId] = app;
                return app;
            }

            function getFieldLabel(fieldList, fieldId) {
                const field = (fieldList || []).find((f) => normalizeId(f.field_id) === normalizeId(fieldId));
                return field ? (field.name || field.field_name || field.title || field.field_id) : fieldId;
            }

            // Builds the display rows (app name/icon, resolved field labels)
            // shown in the table - entry.app_id/phone_field_ids/name_field_id
            // are the only thing actually persisted; everything else here is
            // derived for display, same split as mcpServers.directive.js's
            // buildRowFromItem.
            $scope.rows = [];
            $scope.loadingRows = true;

            async function buildRows() {
                $scope.loadingRows = true;
                safeApply();

                try {
                    const rows = [];

                    for (const entry of $scope.trunk.connected_apps) {
                        try {
                            const app = await getAppData(entry.app_id);
                            const fieldList = Array.isArray(app.field_list) ? app.field_list : [];

                            rows.push({
                                entry,
                                app,
                                // phone_field_ids is a comma-separated string of
                                // field_ids (gh-input-list's own native storage
                                // format - see the add/edit dialog below), not an
                                // array.
                                phoneFieldLabels: String(entry.phone_field_ids || '')
                                    .split(',')
                                    .filter(Boolean)
                                    .map((fieldId) => getFieldLabel(fieldList, fieldId))
                                    .join(', '),
                                nameFieldLabel: entry.name_field_id ? getFieldLabel(fieldList, entry.name_field_id) : ''
                            });
                        } catch (error) {
                            console.log('Connected app row load error:', entry.app_id, error);
                        }
                    }

                    $scope.rows = rows;
                } finally {
                    $scope.loadingRows = false;
                    safeApply();
                }
            }

            // $watchCollection's listener also fires once on the very next
            // digest after registration (same as a plain $watch), so this
            // alone covers both the initial row build and any later
            // add/edit/delete.
            $scope.$watchCollection('trunk.connected_apps', function (newValue) {
                if (!Array.isArray(newValue)) {
                    $scope.trunk.connected_apps = [];
                    return;
                }

                buildRows();
            });

            async function persistConnectedApps(connectedApps) {
                const updated = await SipTrunksService.update($scope.trunk.trunk_id, { connected_apps: connectedApps });
                Object.assign($scope.trunk, updated);
                safeApply();
                $scope.onUpdate();
            }

            // Opens a dialog to add a new connected app, or edit an existing
            // one when `existingEntry` (the exact array item reference) is
            // passed - same add/edit-share-one-dialog shape as
            // trunk-numbers.tab.js's openNumberDialog.
            function openConnectedAppDialog(existingEntry) {
                const existingConnectedApps = $scope.trunk.connected_apps;

                GhDialog.show({
                    position: 'center',
                    toolbar: false,
                    template: {
                        toolbar: '',
                        resizable: false,
                        content: `
                            <div class="add_connected_app-popup" style="padding: 20px; position: relative; min-width: 420px; max-width: 480px;">
                                <span gh-icon="cross 0D99FF 20px" style="position: absolute; top: 4px; right: 4px; cursor: pointer;" ng-click="cancel()"></span>
                                <h3 style="font-size: 18px; font-weight: 500; margin-bottom: 16px; color: #333;">{{isEditing ? translate('Edit Connected App') : translate('Add Connected App')}}</h3>

                                <label style="font-size: 14px; color: #666; display: block; margin-bottom: 6px;">{{translate('Application')}}</label>
                                <div style="margin-bottom: 16px;">
                                    <gh-input
                                        gh-dropdown
                                        autocomplete="appsList"
                                        ng-model="form.app_id"
                                        gh-data-type="text_opt"
                                        size="large"
                                    ></gh-input>
                                </div>

                                <!--
                                    Both pickers below use gh-input('-list')'s built-in
                                    autocomplete="fieldsList <appId>" fetch (same one
                                    gh_elements/field/field_data.js uses for its own
                                    app-field picker) to list the selected app's fields.
                                    gh-data-type is deliberately "text_opt", not "field" -
                                    "field" ties the picked value into GudHub's live field
                                    CRUD pipe (FieldProcessor's gh_model_get/update/delete
                                    listeners), which is for editing an app's OWN field
                                    schema, not for picking a field_id reference like this.
                                    Using it here threw "Cannot read properties of null
                                    (reading 'app_id')" from FieldProcessor.js on every
                                    gh-input-list row add/remove. "text_opt" (the same
                                    data_type this tab's own app picker above already
                                    uses) renders picked values as plain text/options with
                                    no such side effects, and still supports gh-input-list's
                                    row-item label rendering (see gh_elements/text_options_data.js's
                                    own 'input_list' interpretation).

                                    gh-input-list also compiles its inner <gh-input> against
                                    ITS OWN isolate scope, so the autocomplete attribute's
                                    {{}} expression must read $parent.form.app_id, not
                                    form.app_id directly, to reach this dialog's own scope -
                                    field_data.js's identical "multiple" field picker does
                                    the same ($parent.field_model...). The plain gh-input
                                    below does not need this - it evaluates its own
                                    autocomplete attribute against scope.$parent internally
                                    (see gh_input.js's 'fieldsList' case).
                                -->
                                <div ng-if="form.app_id">
                                    <label style="font-size: 14px; color: #666; display: block; margin-bottom: 6px;">{{translate('Phone number field(s)')}}</label>

                                    <gh-input-list
                                        gh-editable="true"
                                        add-button-name="{{translate('Add field')}}"
                                        gh-field="phoneFieldModel"
                                        gh-model="form.phone_field_ids"
                                        gh-dropdown
                                        autocomplete="fieldsList {{$parent.form.app_id}}"
                                        gh-data-type="text_opt"
                                        size="large"
                                    ></gh-input-list>

                                    <label style="font-size: 14px; color: #666; display: block; margin: 16px 0 6px;">{{translate('Caller name field')}}</label>

                                    <gh-input
                                        gh-editable="true"
                                        ng-model="form.name_field_id"
                                        gh-dropdown
                                        autocomplete="fieldsList {{form.app_id}}"
                                        gh-data-type="text_opt"
                                        size="large"
                                    ></gh-input>
                                </div>

                                <div ng-show="errMessage" style="color: #f44336; font-size: 13px; margin-top: 12px;">{{errMessage}}</div>

                                <div style="display: flex; gap: 10px; justify-content: center; margin-top: 20px;">
                                    <button class="btn btn-blue-reverse" ng-click="cancel()">{{translate('Cancel')}}</button>
                                    <button class="btn btn-blue" ng-click="save()" ng-disabled="saving">{{isEditing ? translate('Save') : translate('Add')}}</button>
                                </div>
                            </div>
                        `
                    },
                    locals: {
                        existingConnectedApps,
                        existingEntry: existingEntry || null
                    },
                    controller: ['$scope', 'existingConnectedApps', 'existingEntry', 'localizationService', function ($scope, existingConnectedApps, existingEntry, localizationService) {
                        const dictionary = localizationService.getDictionary('account_settings');
                        $scope.translate = (word) => localizationService.translate(word, dictionary);
                        $scope.isEditing = !!existingEntry;

                        $scope.form = {
                            app_id: existingEntry ? normalizeId(existingEntry.app_id) : '',
                            // Both gh-input-list (phone_field_ids) and gh-input
                            // (name_field_id) store field_id(s) directly as
                            // string(s) - gh-input-list joins multiple picks with
                            // a comma (see gui/gh_input_list/gh_input_list.js's
                            // proxyModel setter) - so these are read/written
                            // as-is, no array<->string translation needed.
                            phone_field_ids: existingEntry ? String(existingEntry.phone_field_ids || '') : '',
                            name_field_id: existingEntry ? normalizeId(existingEntry.name_field_id) : ''
                        };

                        $scope.errMessage = '';
                        $scope.saving = false;

                        // gh-input-list's row-item labels are rendered against
                        // whatever app_id lives on the gh-field it's given -
                        // kept in sync here since
                        // gh-input-list itself only reads this once at link time.
                        $scope.phoneFieldModel = { data_model: { app_id: $scope.form.app_id } };

                        $scope.$watch('form.app_id', function (newVal) {
                            $scope.phoneFieldModel.data_model.app_id = newVal;
                        });

                        $scope.save = async function () {
                            $scope.errMessage = '';

                            const appId = normalizeId($scope.form.app_id);

                            if (!appId) {
                                $scope.errMessage = 'Please select an application';
                                return;
                            }

                            if (!$scope.form.phone_field_ids) {
                                $scope.errMessage = 'Please select at least one phone number field';
                                return;
                            }

                            $scope.saving = true;

                            try {
                                const newEntry = {
                                    app_id: appId,
                                    phone_field_ids: $scope.form.phone_field_ids,
                                    name_field_id: $scope.form.name_field_id ? normalizeId($scope.form.name_field_id) : null
                                };

                                const updatedList = existingEntry
                                    ? existingConnectedApps.map((e) => e === existingEntry ? newEntry : e)
                                    : [...existingConnectedApps, newEntry];

                                await persistConnectedApps(updatedList);
                                $scope.hide();
                            } catch (error) {
                                console.log('Save connected app error:', error);
                                $scope.errMessage = error.message || 'Failed to save connected app';
                                $scope.saving = false;
                                $scope.$applyAsync();
                            }
                        };
                    }]
                });
            }

            $scope.addConnectedApp = function () {
                openConnectedAppDialog(null);
            };

            $scope.editConnectedApp = function (row) {
                openConnectedAppDialog(row.entry);
            };

            // Shows a confirmation dialog and removes the connected app on confirm.
            $scope.deleteConnectedApp = function (row) {
                const remaining = $scope.trunk.connected_apps.filter((e) => e !== row.entry);

                GhDialog.show({
                    position: 'center',
                    toolbar: false,
                    template: {
                        toolbar: '',
                        resizable: false,
                        content: `
                            <div style="padding: 20px; position: relative; min-width: 360px; text-align: center;">
                                <span gh-icon="cross 0D99FF 20px" style="position: absolute; top: 4px; right: 4px; cursor: pointer;" ng-click="cancel()"></span>
                                <h3 style="font-size: 18px; font-weight: 500; margin-bottom: 12px; color: #333;">{{translate('Delete Connected App')}}</h3>
                                <p style="font-size: 14px; color: #666; margin-bottom: 20px;">{{translate('Are you sure you want to disconnect')}} <strong>{{appName}}</strong>?</p>
                                <div style="display: flex; gap: 10px; justify-content: center;">
                                    <button class="btn btn-blue-reverse" ng-click="cancel()">{{translate('Cancel')}}</button>
                                    <button class="btn btn-red" ng-click="confirm()" ng-disabled="deleting">{{translate('Delete')}}</button>
                                </div>
                            </div>
                        `
                    },
                    locals: {
                        appName: row.app.app_name || row.entry.app_id
                    },
                    controller: ['$scope', 'appName', 'localizationService', function ($scope, appName, localizationService) {
                        const dictionary = localizationService.getDictionary('account_settings');
                        $scope.translate = (word) => localizationService.translate(word, dictionary);
                        $scope.appName = appName;
                        $scope.deleting = false;

                        $scope.confirm = async function () {
                            $scope.deleting = true;

                            try {
                                await persistConnectedApps(remaining);
                                $scope.hide();
                            } catch (error) {
                                console.log('Delete connected app error:', error);
                                $scope.deleting = false;
                                $scope.$applyAsync();
                            }
                        };
                    }]
                });
            };
        }],
        templateUrl: trunkConnectedAppsTemplateUrl
    };
}]);
